import React, { useCallback } from 'react';

import {
  DescriptionList,
  DescriptionItem,
  Typography,
  Stack,
  Link,
  AccessTimeIcon,
  InfoOutlinedIcon,
  makeStyles,
} from '@gogame/components';
import { useQueryClient } from '@gogame/query';
import { DateValueFormatter } from '@gogame/intl-ts';

import { ConceptReviewsSummary } from 'scenes/Gallery/GalleryPage/Concepts/Reviews';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/view-constants';
import CreativeDetails from 'scenes/Gallery/GalleryPage/Creatives/CreativeDetails';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/view-constants';
import StatusBadge from 'scenes/Gallery/components/StatusBadge';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import { valueParserObjectFromString } from 'scenes/Gallery/GalleryPage/utils/valueParsers';
import BusinessPartnerDetails from 'scenes/BusinessPartners/components/BusinessPartnerDetails';

import PlannedSchedule from './components/PlannedSchedule';

const useStyles = makeStyles(() => ({
  overflowWrap: {
    overflowWrap: 'anywhere', //todo move to description list component
  },
}));

const ConceptDetailsInfo = ({
  data = {},
  variant,
  openChildComponent,
  termWidth,
}) => {
  const classes = useStyles();

  const queryClient = useQueryClient();

  const {
    isAllowRequestedBy,
    isAllowDesigner,
    isAllowSharedWith,
    isAllowBusinessPartner,
    isAllowStatuses,
  } = useGalleryPermissions();

  const {
    [CONCEPT.ID]: conceptId,
    [CONCEPT.IS_PARTIAL_SHARED]: isPartialShared,
    [CONCEPT.SHARE_COUNT]: shareCount,
    [CONCEPT.CREATIVES_COUNT]: creativesCount,
    [CONCEPT.STATE_SCHEDULES]: stateSchedules,
    [CONCEPT.ORIGINAL]: {
      [CREATIVE.OUTSOURCE_STUDIO]: originalOutsourceStudio,
      [CREATIVE.DESIGNER]: originalDesigner,
    },
  } = data;

  const handleOpenBP = useCallback(
    (id) => {
      openChildComponent(BusinessPartnerDetails, {
        id,
        onChange: () => {
          queryClient.resetQueries({ active: true });
        },
      });
    },
    [openChildComponent, BusinessPartnerDetails, queryClient]
  );

  const handleOpenCreative = useCallback(
    (id) => {
      openChildComponent(CreativeDetails, {
        id,
        onChange: () => {
          queryClient.resetQueries({ active: true });
        },
      });
    },
    [openChildComponent, CreativeDetails, queryClient]
  );

  return (
    <DescriptionList
      variant={variant}
      data={data}
      stripEmpty={true}
      disableGutters={true}
      termWidth={termWidth}
    >
      {isAllowStatuses && (
        <DescriptionItem
          label={'Status'}
          field={CONCEPT.STATUS_ID}
          valueParser={valueParserObjectFromString}
          type={'status'}
          statusFactory={
            stateSchedules?.length > 1
              ? ({ id }) => (
                  <Stack spacing={1} direction={'row'} alignItems={'center'}>
                    <StatusBadge id={id}>
                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                    </StatusBadge>
                    <PlannedSchedule
                      data={stateSchedules}
                      title={'Schedule'}
                      icon={
                        <InfoOutlinedIcon
                          sx={{ fontSize: 18 }}
                          color={'action'}
                        />
                      }
                    />
                  </Stack>
                )
              : StatusBadge
          }
        />
      )}
      <DescriptionItem
        label={'Rating'}
        field={CONCEPT.RATING}
        type={({ value }) => (
          <ConceptReviewsSummary
            conceptId={conceptId}
            rating={value}
            variant={variant}
          />
        )}
      />
      {isAllowRequestedBy ? (
        <DescriptionItem
          label={'Requested by'}
          value={[data[CONCEPT.REQUESTED_BY], data[CONCEPT.JIRA_TASK]]}
          zeroMinWidth={true}
          type={({ value: [requestedBy, jiraTask] }) =>
            jiraTask ? (
              <Typography noWrap={true} tooltipText={'Link to Jira Task'}>
                <Link href={jiraTask} target={'_blank'}>
                  {requestedBy}
                </Link>
              </Typography>
            ) : (
              <Typography>{requestedBy}</Typography>
            )
          }
        />
      ) : null}
      {isAllowBusinessPartner ? (
        <DescriptionItem
          label={'Business partner'}
          field={CONCEPT.BUSINESS_PARTNERS}
          type={'link'}
          linkLabelField={'[0].name'}
          onValueClick={(_e, [{ id }]) => handleOpenBP(id)}
        />
      ) : null}
      {isAllowSharedWith ? (
        <DescriptionItem
          label={'Shared with'}
          field={CONCEPT.SHARED_WITH}
          type={'typographyList'}
          unitsLabel={'Business Partners'}
          limit={1}
          listType={'link'}
          item={({ id, name }) => (
            <Link onClick={() => handleOpenBP(id)}>
              <Typography lineClamp={2} className={classes.overflowWrap}>
                {name}
              </Typography>
            </Link>
          )}
        />
      ) : null}
      <DescriptionItem
        label={'Moved creatives'}
        field={CONCEPT.MOVED_CREATIVES}
        type={'typographyList'}
        unitsLabel={'creatives'}
        limit={1}
        listType={'link'}
        item={({ id, name, moveDate }) => (
          <Stack direction={'column'}>
            <Link onClick={() => handleOpenCreative(id)}>
              <Typography lineClamp={2} className={classes.overflowWrap}>
                {name}
              </Typography>
            </Link>
            <Typography>
              <DateValueFormatter
                value={moveDate}
                template={'LLL'}
                timeZone={'local'}
              />
            </Typography>
          </Stack>
        )}
      />
      {isPartialShared && isAllowSharedWith ? (
        <DescriptionItem
          label={'Shared partially'}
          value={`${shareCount}/${creativesCount} creatives`}
        />
      ) : null}
      {isAllowDesigner ? (
        <DescriptionItem
          label={'Designer'}
          value={
            originalOutsourceStudio
              ? `${originalOutsourceStudio.name} (Outsource studio)`
              : originalDesigner
          }
          type={({ value }) => (
            <Typography
              sx={{
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
          )}
        />
      ) : null}
      <DescriptionItem
        label={'Last updated by'}
        type={'metadata'}
        value={data}
        userField={CONCEPT.UPDATED_BY}
        dateField={CONCEPT.UPDATED_DATE}
      />
      <DescriptionItem
        label={'Uploaded by'}
        type={'metadata'}
        value={data}
        userField={CONCEPT.CREATED_BY}
        dateField={CONCEPT.CREATED_DATE}
      />
    </DescriptionList>
  );
};

export default ConceptDetailsInfo;
